import { useState, useEffect } from 'react';
import { SurveyFieldGroup } from '../features/surveys/types';
import { RequiredFieldsLegend } from './RequiredFieldsLegend';

interface Props {
  group?: SurveyFieldGroup;
  onSubmit: (data: { name: string; weight: number; threshold?: number | null }) => void;
  onCancel: () => void;
  loading?: boolean;
}

export function SurveyFieldGroupEditor({ group, onSubmit, onCancel, loading }: Props) {
  const [name, setName] = useState(group?.name || '');
  const [weight, setWeight] = useState(group?.weight?.toString() || '0');
  const [threshold, setThreshold] = useState(group?.threshold?.toString() ?? '');

  useEffect(() => {
    if (group) {
      setName(group.name);
      setWeight(group.weight.toString());
      setThreshold(group.threshold != null ? group.threshold.toString() : '');
    }
  }, [group]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const thresholdValue = threshold.trim() === '' ? null : parseFloat(threshold);
    onSubmit({
      name,
      weight: parseFloat(weight) || 0,
      threshold: thresholdValue != null && !Number.isNaN(thresholdValue) ? thresholdValue : null,
    });
  };

  const submitLabel = group ? 'Actualizar' : 'Crear';

  return (
    <form onSubmit={handleSubmit} className="space-y-4 border border-border rounded-lg p-4 bg-card">
      <h3 className="text-lg font-semibold">
        {group ? 'Editar Agrupación' : 'Nueva Agrupación'}
      </h3>
      
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Nombre de la Agrupación *
        </label>
        <input
          type="text"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej: Gestión Comercial"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Ponderación (%) *
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          max="100"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          placeholder="0-100"
          required
        />
        <p className="text-xs text-muted-foreground mt-1">
          Porcentaje de incidencia sobre el total (0-100)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Umbral mínimo (opcional)
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          max="10"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          placeholder="Ej: 6.5"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Puntaje ponderado mínimo aceptable para esta agrupación (0-10)
        </p>
      </div>

      <RequiredFieldsLegend />
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-muted transition-colors"
          disabled={loading}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          disabled={loading}
        >
          {loading ? 'Guardando...' : submitLabel}
        </button>
      </div>
    </form>
  );
}
